import {
  CheckboxVisibility,
  IColumn,
  IObjectWithKey,
  IPlainCardProps,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import AppContext from 'App/AppContext';
import {
  IPActionButton,
  IPHoverCard,
  IPHoverCardType,
  IPLabel,
  IPSelection,
  IPSelectionMode,
  IPShimmeredDetailsList,
  IPText,
} from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import Accordion from 'components/Accordion/Accordion';
import { copyAndSortAssets } from 'components/Asset/AssetListSorting';
import { ClassificationTagList } from 'components/Classification/ClassificationTagList';
import AddNewLink from 'components/Links/AddNewLink';
import HrefLink from 'components/Links/HrefLink';
import PinnedLinkLink from 'components/Links/PinnedLinkLink';
import AssetPicker from 'components/Pickers/AssetPicker';
import { OwnerItem } from 'components/Pickers/OwnerPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { onRenderDetailsHeaderNoPaddingTopGlobal } from 'globalFunctions';
import { deleteIcon, globalStackTokensGapSmall, newIcon } from 'globalStyles';
import { TFunction } from 'i18next';
import Asset from 'models/asset/asset';
import ProcessingActivity from 'models/processingActivity';
import ResourceLink from 'models/resourceLink';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

export enum RelationType {
  Data = 0,
  Recipient = 1,
  Processor = 2,
  JointController = 3,
}

export interface IProcessingActivityAssetListProps {
  label: string;
  assets: Asset[];
  isLoading: boolean;
  isReadOnly: boolean;
  selectedProcessingActivity?: ProcessingActivity;
  info?: string;
  subTitle?: string;
  relationType: RelationType;
  addAssets: (assets: Asset[]) => void;
  removeAsset: (asset: Asset) => void;
  assetFilter: (asset: Asset) => boolean;
  links?: ResourceLink[];
  addLinks?: (links: ResourceLink[]) => void;
  removeLink?: (link: ResourceLink) => void;
  onLinkClick?: (linkName: string) => void;
  allAssets?: Asset[];
}

export const ProcessingActivityAssetList = (props: IProcessingActivityAssetListProps) => {
  const { t } = useTranslation(['translation', 'assets', 'asset']);
  const addAssetButtonId = useId('add-asset-button');
  const appContext = useContext(AppContext);
  const [assets, setAssets] = useState<Asset[]>(props.assets);
  const [showAssetPicker, setShowAssetPicker] = useState<boolean>(false);
  const [currentColumn, setCurrentColumn] = useState<IColumn | undefined>(undefined);
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(undefined);
  const [selection] = useState<Selection<Asset>>(
    new IPSelection({
      selectionMode: SelectionMode.single,
      getKey: (item: Asset) => {
        return item.assetId;
      },
      onSelectionChanged: () => {
        const sel = selection.getSelection()[0] as Asset;
        setSelectedAsset(sel);
      },
    }),
  );

  useEffect(() => {
    setAssets(props.assets);
    if (currentColumn) {
      const newItems = copyAndSortAssets(props.assets, currentColumn, currentColumn.isSortedDescending, appContext);
      setAssets(newItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.assets]);

  useEffect(() => {
    setColumnsList(getColumnsList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.allAssets]);

  const onColumnClick = (
    ev?: React.MouseEvent<HTMLElement>,
    column?: IColumn,
    isForceDescendingEnabled?: boolean,
  ): void => {
    if (!column) return;

    const newColumns: IColumn[] = columnsList.slice(); //new columns
    const currColumn: IColumn = newColumns.filter((currCol) => column?.key === currCol.key)[0];
    if (isForceDescendingEnabled !== undefined) {
      newColumns.forEach((newCol: IColumn) => {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = isForceDescendingEnabled;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = true;
        }
      });
    } else {
      for (const newCol of newColumns) {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = !currColumn.isSortedDescending;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = false;
        }
      }
    }

    setCurrentColumn(currColumn);
    const newItems = copyAndSortAssets(assets, currColumn, currColumn.isSortedDescending, appContext);

    setColumnsList(newColumns);
    setAssets(newItems);
  };

  const addAssets = (assets: Asset[]) => {
    setShowAssetPicker(false);
    props.addAssets(assets);
  };

  const removeAsset = (asset?: Asset) => {
    if (!asset) return;
    setShowAssetPicker(false);
    props.removeAsset(asset);
  };

  //
  // Columns list view
  //
  const getColumnsList = (): IColumn[] => {
    const retentionPeriodColumn: IColumn = {
      key: 'retentionPeriod',
      name: t('asset:TabDetails.Fields.RetentionPeriod.Label'),
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item?: Asset, index?: number, column?: IColumn) => {
        return <IPText>{item?.retentionPeriod?.value}</IPText>;
      },
    };

    const childrenColumn: IColumn = {
      key: 'children',
      name: t('asset:TabDetails.Fields.Children'),
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item?: Asset, index?: number, column?: IColumn) => {
        const childAssetCount = item
          ? props.allAssets?.filter((a) => a.containers?.some((c) => c.assetId === item.assetId)).length ?? 0
          : 0;
        const plainCardProps: IPlainCardProps = {
          onRenderPlainCard: () => onRenderAssetChildren(item, index, column),
          renderData: item,
        };
        if (childAssetCount === 0) return null;

        return (
          <IPHoverCard type={IPHoverCardType.plain} instantOpenOnClick={true} plainCardProps={plainCardProps}>
            <IPText variant="small">
              <HrefLink underline>{t('translation:General.List.ItemCount', { count: childAssetCount })}</HrefLink>
            </IPText>
          </IPHoverCard>
        );
      },
    };

    const allColumns = [
      {
        key: 'code',
        name: t('assets:TabOverView.Columns.Code'),
        minWidth: 100,
        maxWidth: 100,
        isSorted: true,
        isSortedDescending: false,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.code}</IPText>;
        },
      },
      {
        key: 'name',
        name: t('assets:TabOverView.Columns.Name'),
        minWidth: 150,
        maxWidth: 400,
        isRowHeader: true,
        isResizable: true,
        isMultiline: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          if (!item) return;

          return (
            <IPText>
              <HrefLink href={`/asset/${item.assetId}/details`} underline>
                {item.name}
              </HrefLink>
            </IPText>
          );
        },
      },
      {
        key: 'state',
        name: t('assets:TabOverView.Columns.State'),
        minWidth: 100,
        maxWidth: 100,
        isResizable: true,
        isCollapsible: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.getStateText(t as TFunction<string[]>)}</IPText>;
        },
      },
      {
        key: 'location',
        name: t('asset:TabDetails.Fields.Location.Label'),
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.location?.value}</IPText>;
        },
      },
      ...(props.relationType === RelationType.Data ? [retentionPeriodColumn] : []),
      ...(props.relationType !== RelationType.Data && hasUserFeature(appContext, FeatureTypes.SupplyChain)
        ? [childrenColumn]
        : []),
      {
        key: 'tags',
        name: t('assets:TabOverView.Columns.Tags'),
        minWidth: 200,
        maxWidth: 400,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <KeyValueTags tags={appContext.globalDataCache.tags.getItemsForId(item?.tagIds)} />;
        },
      },
      {
        key: 'classifications',
        name: t('assets:TabOverView.Columns.Classifications'),
        minWidth: 230,
        maxWidth: 400,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <ClassificationTagList classifications={item?.classifications ?? []} />;
        },
      },
      {
        key: 'owner',
        name: t('assets:TabOverView.Columns.Owner'),
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <OwnerItem owner={item} />;
        },
      },
    ];

    return allColumns;
  };

  const [columnsList, setColumnsList] = useState<IColumn[]>(getColumnsList());

  const getLocalStateKey = (): LocalStorageKeys | undefined => {
    let key: LocalStorageKeys;
    switch (props.relationType) {
      case RelationType.Data:
        key = LocalStorageKeys.ProcessingActivityAssetListAccordionData;
        break;
      case RelationType.JointController:
        key = LocalStorageKeys.ProcessingActivityAssetListAccordionJointController;
        break;
      case RelationType.Recipient:
        key = LocalStorageKeys.ProcessingActivityAssetListAccordionRecipient;
        break;
      case RelationType.Processor:
        key = LocalStorageKeys.ProcessingActivityAssetListAccordionProcessor;
        break;
      default:
        return undefined;
    }

    return key;
  };

  const getDefaultActiveFromLocalState = (): boolean => {
    const key = getLocalStateKey();
    if (!key) return false;
    const localState = getLocalStorageData(appContext, key);

    return localState === 'true';
  };

  const setDefaultActiveToLocalState = (expanded: boolean) => {
    const key = getLocalStateKey();
    if (!key) return;
    setLocalStorageData(appContext, key, expanded.toString());
  };

  const onRenderAssetChildren = (item?: Asset, index?: number, column?: IColumn): JSX.Element => {
    const childAssetIds = item
      ? props.allAssets?.filter((a) => a.containers?.some((c) => c.assetId === item.assetId)).map((a) => a.assetId) ??
        []
      : [];
    const allColumns = [
      {
        key: 'code',
        name: t('assets:TabOverView.Columns.Code'),
        minWidth: 30,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.code}</IPText>;
        },
      },
      {
        key: 'name',
        name: t('assets:TabOverView.Columns.Name'),
        minWidth: 180,
        isRowHeader: true,
        isMultiline: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.name}</IPText>;
        },
      },
      {
        key: 'type',
        name: t('assets:TabOverView.Columns.Type'),
        minWidth: 90,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.getTypeText(t as TFunction<string[]>)}</IPText>;
        },
      },
    ];

    return (
      <IPStack verticalFill styles={{ root: { width: 400, minHeight: 200, maxHeight: 400, padding: 8 } }}>
        <ShimmeredDetailsList
          selectionMode={SelectionMode.none}
          items={props.allAssets?.filter((asset) => childAssetIds.includes(asset.assetId)) ?? []}
          columns={allColumns}
          checkboxVisibility={CheckboxVisibility.hidden}
        />
      </IPStack>
    );
  };

  //
  // Main render
  //
  return (
    <Accordion
      title={props.label}
      showBorder={true}
      summary={assets.length.toString()}
      info={props.info}
      defaultActive={getDefaultActiveFromLocalState()}
      onToggle={(expanded) => setDefaultActiveToLocalState(expanded)}
    >
      <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
        {/*Attach documents section*/}
        {props.links && props.addLinks && props.removeLink && props.onLinkClick && (
          <IPStack.Item style={{ paddingLeft: 5 }}>
            <IPLabel>{props.subTitle}</IPLabel>
            <IPStack horizontalAlign="start" horizontal>
              <IPStack.Item>
                <AddNewLink
                  addLinks={props.addLinks}
                  existingLinks={props.links}
                  disabled={props.isReadOnly}
                  hideWholeList={true}
                  allowCustomLists={true}
                  allowDocumentLibraries={true}
                  allowWebURLs={false}
                  allowSitePages={true}
                  entityContext={true}
                  showCategoryContents={true}
                />
              </IPStack.Item>
              {props.links.map((link) => (
                <IPStack.Item key={link.linkId} style={{ marginLeft: 24 }}>
                  <PinnedLinkLink
                    key={link.linkId}
                    link={link}
                    onClick={(link) => props.onLinkClick!(link.linkName)}
                    onDelete={(link) => props.removeLink!(link)}
                    readonly={props.isReadOnly}
                  />
                </IPStack.Item>
              ))}
            </IPStack>
          </IPStack.Item>
        )}

        {!props.isReadOnly && (
          <IPStack.Item>
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPActionButton
                className="isoplanner-risk-asset-add"
                iconProps={newIcon}
                id={addAssetButtonId}
                onClick={() => setShowAssetPicker(true)}
              >
                {t('processingActivity:TabDetails.AssetList.AddFromList')}
              </IPActionButton>
              <IPActionButton
                className="isoplanner-risk-asset-remove"
                disabled={!selectedAsset}
                iconProps={deleteIcon}
                onClick={() => removeAsset(selectedAsset)}
              >
                {t('processingActivity:TabDetails.AssetList.Remove')}
              </IPActionButton>
              {showAssetPicker && (
                <AssetPicker
                  isOpen={showAssetPicker}
                  target={`#${addAssetButtonId}`}
                  onDismiss={() => setShowAssetPicker(false)}
                  onSelect={addAssets}
                  existingAssets={assets.map((a) => a.assetId)}
                  existingLinks={[]}
                  assetFilter={props.assetFilter}
                  showLocationColumn={true}
                />
              )}
            </IPStack>
          </IPStack.Item>
        )}
        {assets.length > 0 && (
          <IPStack.Item>
            <IPShimmeredDetailsList
              compact
              items={assets}
              enableShimmer={props.isLoading}
              isHeaderVisible={true}
              columns={columnsList}
              shimmerLines={3}
              selection={selection as IPSelection<IObjectWithKey>}
              selectionMode={IPSelectionMode.single}
              setKey="set"
              layoutMode={IPDetailsListLayoutMode.justified}
              selectionPreservedOnEmptyClick={true}
              onRenderDetailsHeader={onRenderDetailsHeaderNoPaddingTopGlobal}
              onColumnHeaderClick={onColumnClick}
            />
          </IPStack.Item>
        )}
      </IPStack>
    </Accordion>
  );
};
